/**
 * Modal that pops up during 08:30–09:30 IST (first half) and 18:00–19:00 IST (second half)
 * whenever the logged-in staff member hasn't yet marked that half's attendance.
 *
 * Polls /api/attendance/window every 60s so the popup appears as soon as the window opens
 * and disappears once marked. Missing the window = auto-absent (server enforces).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { CheckCircle, Clock, Warning, Coffee, MoonStars, MapPin } from "@phosphor-icons/react";
import { markAttendance } from "../lib/attendance";
import { playChime } from "../lib/chime";
import { SelfieCapture } from "./SelfieCapture";

export default function AttendancePopup() {
  const { user } = useAuth();
  const [win, setWin] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState({ first: false, second: false });
  const selfieRef = useRef(null);
  const chimedRef = useRef({ first: false, second: false });

  const canMark = user?.role === "manager" || user?.role === "member";

  const poll = useCallback(async () => {
    if (!canMark || !user?.profile_complete) return;
    try {
      const { data } = await http.get("/attendance/window");
      setWin(data);
    } catch (err) {
      // Silent polling: log for devtools instead of toasting every 60 s.
      console.error("attendance/window poll failed", err);
    }
  }, [canMark, user?.profile_complete]);

  useEffect(() => {
    if (!canMark) return;
    poll();
    const t = setInterval(poll, 60_000); // every minute
    return () => clearInterval(t);
  }, [canMark, poll]);

  // Determine which half we should prompt for RIGHT NOW.
  let promptHalf = null;
  if (win && !win.is_holiday) {
    if (win.first_half_open && !win.already_marked.first && !dismissed.first) promptHalf = "first";
    else if (win.second_half_open && !win.already_marked.second && !dismissed.second) promptHalf = "second";
  }

  useEffect(() => {
    if (promptHalf && !chimedRef.current[promptHalf]) {
      chimedRef.current[promptHalf] = true;
      playChime();
    }
  }, [promptHalf]);

  if (!canMark || !win) return null;
  if (win.is_holiday) return null;

  const mark = async (half) => {
    setSaving(true);
    try {
      await markAttendance(half, selfieRef.current?.capture() || {});
      toast.success(`${half.charAt(0).toUpperCase() + half.slice(1)} half marked present`);
      await poll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => setDismissed((d) => ({ ...d, [promptHalf]: true }));

  const isOpen = promptHalf !== null;
  const Icon = promptHalf === "first" ? Coffee : MoonStars;
  const label = promptHalf === "first" ? "Morning Check-In" : "Evening Check-In";
  const windowLabel = promptHalf === "first" ? "08:30 – 09:30 IST" : "18:00 – 19:00 IST";

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-md"
        data-testid={`attendance-popup-${promptHalf}`}
      >
        <DialogHeader>
          <div className="label-overline text-orange-600 mb-1 flex items-center gap-1.5">
            <Clock size={12} weight="bold" /> Attendance · {windowLabel}
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase flex items-center gap-2">
            <Icon size={22} weight="bold" className="text-orange-600" />
            {label}
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-zinc-700 mt-3">
          Please confirm you&apos;re on the clock for this half of the day. If you skip this window,
          the system will record you as absent for the <strong>{promptHalf}</strong> half.
        </div>

        {/* 1–2 day overdue nudge (server hides the popup entirely at 3+ days) */}
        {win.overdue_days >= 1 && win.overdue_days < 3 && (
          <div
            className="mt-4 border-2 border-red-600 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2"
            data-testid="attendance-overdue-banner"
          >
            <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
            <span>
              You have <strong>{win.overdue_days}</strong>-day{win.overdue_days > 1 ? "s" : ""} overdue task
              {win.overdue_titles?.length > 1 ? "s" : ""}
              {win.overdue_titles?.length ? ` (${win.overdue_titles.slice(0, 2).join(", ")}${win.overdue_titles.length > 2 ? "…" : ""})` : ""}.
              <br />Please close overdue tasks first otherwise you are unable to mark present from day 3 onward.
            </span>
          </div>
        )}

        <div className="mt-4 border border-amber-400 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2">
          <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
          <span>You can only mark attendance during this window. Miss it → auto-absent.</span>
        </div>

        <div className="mt-2 border border-zinc-300 bg-zinc-50 p-3 text-xs text-zinc-700 flex items-start gap-2"
             data-testid="attendance-geo-notice">
          <MapPin size={14} weight="bold" className="mt-0.5 shrink-0 text-orange-600" />
          <span>Your GPS location, IP address, device and a check-in selfie are recorded with this mark. Please allow location and camera access when prompted.</span>
        </div>

        {isOpen && <SelfieCapture ref={selfieRef} />}

        <div className="flex justify-end gap-2 pt-4 border-t border-zinc-300 mt-4">
          <Button
            type="button" variant="outline" onClick={dismiss}
            className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100"
            data-testid={`attendance-later-${promptHalf}`}
          >
            Remind Later
          </Button>
          <Button
            type="button" disabled={saving} onClick={() => mark(promptHalf)}
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs px-5"
            data-testid={`attendance-mark-${promptHalf}`}
          >
            <CheckCircle size={12} weight="bold" className="mr-1.5" />
            {saving ? "Marking…" : "Mark Present"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
